// Reading the Accelerator's locked My Offer — pure functions, no storage.
//
// This half of the old offerStore.js is deliberately free of persistence. It
// takes locked-output text in and returns plain data out, so it stays
// synchronous and directly testable no matter where offers are stored. When
// `offerStore` moved from localStorage to Supabase, everything here was
// unaffected — that's the point of the split.
//
// `offerStore.js` re-exports all of this, so existing imports keep working.

import { hashSource } from "./sourceHash";

export const SOURCE_SECTION = "Your Signature Offer";

export function newOfferId() {
  return "off_" + Math.random().toString(36).slice(2, 10);
}

/* ── Pricing ──────────────────────────────────────────────────────────────────
   `pricing` used to be a single free-text string. It's now a small structure so
   a Continuation tier can say "$97 / month" as data rather than as prose, and so
   the payment-plan answer has somewhere to live.

   `readPricing` is the only way anything should read the field — it accepts the
   old string shape, so offers saved before this change still display. Nothing
   rewrites stored records on read; they upgrade when next saved.

   paymentPlan is deliberately tri-state: null means "not answered", which is
   different from "no". We don't want a pull from the Accelerator to assert an
   answer she never gave.                                                       */
/* ── What KIND of thing this offer is ────────────────────────────────────────
   Her ask, 2026-09-22, and it exists because GoHighLevel requires a
   `productType` on every product — until now the push assumed SERVICE for
   everything, which is right for most of a coaching ladder and wrong for the
   template pack.

   NULL IS NOT "SERVICE". A row written before the column existed was never
   asked, and `productTypeOf` answering "service" is a DISPLAY default, not a
   stored one — nothing writes it until she picks. Same rule as paymentPlan's
   tri-state: a pull from the Accelerator must not assert an answer she never
   gave. */
export const PRODUCT_TYPES = [
  { id: "service", label: "Service", hint: "Coaching, done-with-you, anything you deliver." },
  { id: "digital", label: "Digital", hint: "A course, a template, a download." },
  { id: "physical", label: "Physical", hint: "Something that ships." },
];

export function productTypeOf(offer) {
  const v = String(offer?.productType || "").trim().toLowerCase();
  return PRODUCT_TYPES.some((t) => t.id === v) ? v : "service";
}


export const EMPTY_PRICING = { amount: "", mode: "flat", interval: "month", paymentPlan: null };

export function readPricing(pricing) {
  if (!pricing) return { ...EMPTY_PRICING };
  if (typeof pricing === "string") return { ...EMPTY_PRICING, amount: pricing };
  return { ...EMPTY_PRICING, ...pricing };
}

export function formatPricing(pricing) {
  const p = readPricing(pricing);
  if (!p.amount) return "";
  const base = p.mode === "recurring" ? `${p.amount} / ${p.interval || "month"}` : p.amount;
  return p.paymentPlan ? `${base} · plan` : base;
}

/* ── Reading the locked My Offer ──────────────────────────────────────────────
   The locked `Your Signature Offer` output is prose Brill wrote, not a form
   submission — it reliably contains the seven elements (program name, who it's
   for, promise, format, length, price, founder's close) but not in a guaranteed
   layout. So every extractor here is tolerant and every one is allowed to fail:
   a field we can't read confidently is left empty for her to fill, which is the
   same rule as not guessing the offer type.                                    */

// The exact text the pull was built from. Hashed so we can tell later whether
// she has re-locked My Offer since — see `sourceChanged`.
function sourceTextOf(offerOutputs = {}) {
  return [offerOutputs[SOURCE_SECTION], offerOutputs.Pricing]
    .filter(Boolean).join("\n\n---\n\n");
}

// `hashSource` moved to utils/sourceHash.js when website sections needed the
// same snapshot-and-compare. Same algorithm, so hashes already in Supabase
// still match.

// Strips the markdown Brill writes around a value: **bold**, quotes, trailing
// punctuation, and a leading em-dash the reflection format sometimes uses.
// Also collapses the line wrapping in her prose, so a pulled summary lands in
// the edit form as one paragraph rather than with her line breaks baked in.
function clean(s) {
  return (s || "")
    .replace(/\*/g, "")
    .replace(/\s+/g, " ")
    .replace(/^\s*[:—-]\s*/, "")
    .replace(/^["'“‘]|["'”’]\s*$/g, "")
    .replace(/[.,;]\s*$/, "")
    .trim();
}

// The seven elements PROMPT 4 asks Brill to confirm, plus the words she labels
// them with. Used to recognise a label so one never gets stored as a value.
const LABEL_WORDS =
  "name|promise|price|pricing|investment|cost|format|delivery|length|duration|" +
  "who\\s+it'?s?\\s+for|founder'?s?\\s+close|transformation|summary|structure";

function isLabel(s) {
  return /[:—-]\s*$/.test(s) ||
    new RegExp(`^(?:the\\s+)?(?:program\\s+|offer\\s+)?(?:${LABEL_WORDS})\\b`, "i").test(s);
}

// A labelled line, however Brill decorated it — "**Program name:** X",
// "## 1. THE PROGRAM NAME — X", "Name: X".
function labelled(text, label) {
  const re = new RegExp(
    `(?:^|\\n)[\\s>*#]*(?:\\d[.)]\\s*)?(?:the\\s+)?(?:${label})\\s*\\**\\s*[:\\u2014-][ \\t]*\\**[ \\t]*(.*)`,
    "i",
  );
  const m = re.exec(text || "");
  if (!m) return "";
  const sameLine = clean(m[1]);
  if (sameLine.length > 1) return sameLine;
  // She also writes the label on its own line with the value beneath it. Take
  // that line — but only if it isn't itself the next label, which is the case
  // this guard exists to prevent.
  const after = (text || "").slice(m.index + m[0].length);
  const next = clean((after.split("\n").find((l) => l.trim()) || ""));
  return next.length > 1 && !isLabel(next) ? next : "";
}

function extractName(text) {
  const byLabel = labelled(text, "program\\s+name|offer\\s+name|name\\s+of\\s+(?:the\\s+)?(?:program|offer)");
  if (byLabel) return byLabel;
  // Fall back to a heading, then to a line that is nothing but bold text —
  // both are how Brill titles a locked block.
  // A candidate is only a name if it isn't the section's own title and isn't
  // one of the seven element labels — "**Program Name:**" on its own line is a
  // bold line, but storing it as the offer's name is worse than storing nothing.
  const usable = (s) => s && !/^your\s+signature\s+offer$/i.test(s) && !isLabel(s);
  const heading = /(?:^|\n)#{1,4}\s*(.+)/.exec(text || "");
  if (heading && usable(clean(heading[1]))) return clean(heading[1]);
  const bold = /(?:^|\n)\s*\*\*([^*\n]{3,80})\*\*\s*(?:\n|$)/.exec(text || "");
  if (bold && usable(clean(bold[1]))) return clean(bold[1]);
  return "";
}

function extractSummary(text) {
  const promise = labelled(text, "program\\s+promise|the\\s+promise|promise|transformation");
  if (promise) return promise;
  const who = labelled(text, "who\\s+it'?s?\\s+for|who\\s+it\\s+is\\s+for");
  if (who) return `For ${who.replace(/^for\s+/i, "")}`;
  // Otherwise the first real paragraph — skip headings and label-only lines.
  const para = (text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .find((p) => p.length > 40 && !/^[#>*\s]*[A-Z][A-Za-z'\s]{2,30}\s*[:—-]\s*$/.test(p));
  return para ? clean(para).slice(0, 400) : "";
}

const AMOUNT = /\$\s?[\d,]+(?:\.\d{2})?/;

function extractPricing(signatureText, pricingText) {
  // The Pricing module states the number explicitly — trust it over the price
  // mentioned in passing inside the Signature Offer prose.
  const recommended = /recommended\s+price[^\d$]{0,20}(\$\s?[\d,]+(?:\.\d{2})?)/i.exec(pricingText || "");
  const fromLabel = labelled(signatureText, "price|investment|pricing");
  const anyIn = (t) => (AMOUNT.exec(t || "") || [])[0] || "";
  const amount = (recommended?.[1] || anyIn(fromLabel) || anyIn(pricingText) || anyIn(signatureText) || "")
    .replace(/\s/g, "");
  if (!amount) return { ...EMPTY_PRICING };

  // Recurring only when an interval is attached to *the amount we chose*. The
  // Pricing module talks about other numbers — cost of the pain ("losing
  // $15,000 a year"), the five-and-ten-client math — and matching any of those
  // turned a one-time $6,500 program into a yearly subscription.
  const both = `${signatureText || ""}\n${pricingText || ""}`;
  const digits = amount.replace(/^\$/, "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const rec =
    new RegExp(`\\$\\s?${digits}\\s*(?:\\/|per\\s+|a\\s+|every\\s+)\\s*(month|mo|year|yr|week|wk)\\b`, "i").exec(both)
    // "monthly membership" is explicit enough to stand on its own. "monthly
    // payment" is not — that's how a payment plan on a one-time price reads.
    || /\b(month|year|week)ly\s+(?:subscription|membership|retainer)\b/i.exec(both);
  const unit = (rec?.[1] || "").toLowerCase();
  const interval = /^(year|yr)$/.test(unit) ? "year" : /^(week|wk)$/.test(unit) ? "week" : "month";

  // Only a positive signal sets this. Absence stays null — "she didn't say",
  // not "no". The Pricing module's "Payment structure:" heading is not itself a
  // signal, because it's equally where she says pay-in-full only.
  const plan = /\bpayment\s+plan\b|\binstallments?\b|\bpayments?\s+of\s+\$|\b\d+\s+(?:monthly\s+)?payments?\b|\bpay\s+in\s+full\s+or\b|\bsplit\s+into\b/i
    .test(both) ? true : null;

  return { amount, mode: rec ? "recurring" : "flat", interval, paymentPlan: plan };
}

/**
 * Is there a locked signature offer to pull at all? Cheap on purpose — the UI
 * asks this on render to decide whether to offer the action, so it checks for
 * the source section without doing any of the parsing work.
 */
export function hasSignatureOffer(offerOutputs = {}) {
  return Boolean((offerOutputs[SOURCE_SECTION] || "").trim());
}

/** What the pull would produce from a set of locked My Offer outputs. */
export function readSignatureOffer(offerOutputs = {}) {
  if (!hasSignatureOffer(offerOutputs)) return null;
  const signature = offerOutputs[SOURCE_SECTION];
  const pricingText = offerOutputs.Pricing || "";
  const sourceText = sourceTextOf(offerOutputs);
  return {
    name: extractName(signature),
    summary: extractSummary(signature),
    pricing: extractPricing(signature, pricingText),
    sourceText,
    sourceHash: hashSource(sourceText),
  };
}

/** True when My Offer has been re-locked since this offer was pulled. */
export function sourceChanged(offer, offerOutputs = {}) {
  if (!offer || offer.source !== "accelerator" || !offer.sourceHash) return false;
  const current = sourceTextOf(offerOutputs);
  if (!current) return false;
  return hashSource(current) !== offer.sourceHash;
}

/* When the prose never names the program, the row is still hers to rename — so it
   gets the section's own name rather than landing blank and looking unfinished.
   This lives OUTSIDE `readSignatureOffer` on purpose: the reader has to stay
   honest about not knowing, because `resyncOffer` falls back to her existing name
   when the read comes up empty. A reader that invented this string would
   overwrite a name she'd already changed. */
export const DEFAULT_CORE_NAME = "Signature Offer";
